import type { Country, Zone } from "@/types";

/**
 * Which deployment an institution lives in.
 *
 * **Derived from country, never chosen.** Zone decides where the data
 * physically sits and which law reaches it, and letting somebody pick it from a
 * dropdown is how a German school ends up in an American datacentre because of
 * a mis-click on a Tuesday.
 *
 * The map is exhaustive over `Country`, mirroring the backend, so adding a
 * country without deciding its zone is a compile error rather than a silent
 * default into the wrong jurisdiction.
 */
const COUNTRY_ZONE: Record<Country, Zone> = {
  DE: "eu", AT: "eu", CH: "eu", GB: "eu", IE: "eu", NL: "eu", BE: "eu",
  FR: "eu", ES: "eu", IT: "eu", PL: "eu", SE: "eu", NO: "eu", DK: "eu",
  FI: "eu", US: "eu", CA: "eu",
};

export function zoneForCountry(country: Country): Zone {
  return COUNTRY_ZONE[country];
}

/**
 * The zones that exist today.
 *
 * One. The US zone is real in the design and not in the world: American and
 * Canadian institutions currently live in the EU deployment, which is a
 * decision with consequences a customer is entitled to hear about rather than
 * discover. The form shows US, disabled, with the reason.
 */
export const AVAILABLE_ZONES: readonly Zone[] = ["eu"] as const;

export function zoneIsAvailable(zone: Zone): boolean {
  return AVAILABLE_ZONES.includes(zone);
}

export const COUNTRIES: readonly Country[] = [
  "DE", "AT", "CH", "GB", "IE", "NL", "BE", "FR", "ES", "IT",
  "PL", "SE", "NO", "DK", "FI", "US", "CA",
] as const;

export type InstitutionState = "active" | "suspended" | "lapsed" | "expiring";

/** Fourteen days, the same window the back office warns an owner about. */
export const EXPIRY_WARNING_DAYS = 14;

/**
 * What state an institution is in, from this console's point of view.
 *
 * Suspension outranks expiry: a suspended institution is one somebody decided
 * about, and showing "licence expiring" over the top of that would bury the
 * decision under a clock.
 */
export function institutionState(
  institution: { validUntil: string; suspendedAt?: string },
  now: number
): InstitutionState {
  if (institution.suspendedAt) return "suspended";

  const expiry = Date.parse(institution.validUntil);
  if (!Number.isFinite(expiry)) return "active";
  if (expiry < now) return "lapsed";

  const startOfDay = (ms: number) => {
    const date = new Date(ms);
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  };
  const days = Math.round((startOfDay(expiry) - startOfDay(now)) / 86_400_000);
  return days <= EXPIRY_WARNING_DAYS ? "expiring" : "active";
}
